import React, { createContext, useContext, useState, ReactNode, useCallback, useReducer, useEffect } from 'react';
import AIService, { AIModelFactory, StreamCallbacks } from '../services/apiService';
import { Decision, DecisionTree, parseAIResponseToDecisionTree, generateDecisionPrompt } from '../models/decisionTree';
import { v4 as uuidv4 } from 'uuid';

// 增强API设置接口
export interface ApiSettings {
  adapterType: string;
  apiKey: string;
  baseUrl: string;
  model: string;
  prompt: string;
}

// 错误接口
export interface ApiError {
  code: string;
  message: string;
  details?: string;
  timestamp: Date;
}

// 定义状态管理的Action类型
type DecisionAction = 
  | { type: 'SET_LOADING', payload: boolean }
  | { type: 'SET_API_SETTINGS', payload: Partial<ApiSettings> }
  | { type: 'SET_DECISION_TREE', payload: DecisionTree | null }
  | { type: 'SET_ERROR', payload: ApiError | null } 
  | { type: 'SET_PROGRESS', payload: number }
  | { type: 'SET_STREAMING_CONTENT', payload: string }
  | { type: 'APPEND_STREAMING_CONTENT', payload: string }
  | { type: 'RESET_STATE' };

// 状态接口
interface DecisionState {
  apiSettings: ApiSettings;
  decisionTree: DecisionTree | null;
  loading: boolean;
  error: ApiError | null;
  progress: number;
  streamingContent: string;
  question: string;
}

// 初始API设置
const defaultApiSettings: ApiSettings = {
  adapterType: 'openai',
  apiKey: '',
  baseUrl: 'https://api.openai.com/v1/chat/completions',
  model: 'gpt-3.5-turbo',
  prompt: generateDecisionPrompt()
};

// 从本地存储获取设置或使用默认设置
const getSavedApiSettings = (): ApiSettings => {
  try {
    const savedSettings = localStorage.getItem('apiSettings');
    if (savedSettings) {
      return {...defaultApiSettings, ...JSON.parse(savedSettings)};
    }
  } catch (error) {
    console.error('读取保存的API设置时出错:', error);
  }
  return defaultApiSettings;
};

// 尝试从localStorage获取最后的决策树
const getLastDecisionTree = (): DecisionTree | null => {
  try {
    const lastTreeJson = localStorage.getItem('currentDecisionTree');
    if (lastTreeJson) {
      return JSON.parse(lastTreeJson);
    }
  } catch (error) {
    console.error('读取上次决策树时出错:', error);
  }
  return null;
};

// 初始状态
const initialState: DecisionState = {
  apiSettings: getSavedApiSettings(),
  decisionTree: getLastDecisionTree(),
  loading: false,
  error: null,
  progress: 0,
  streamingContent: '',
  question: ''
};

// 状态reducer
const decisionReducer = (state: DecisionState, action: DecisionAction): DecisionState => {
  let newState;
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_API_SETTINGS':
      return { 
        ...state, 
        apiSettings: { ...state.apiSettings, ...action.payload } 
      };
    case 'SET_DECISION_TREE':
      newState = { ...state, decisionTree: action.payload };
      // 保存到localStorage
      if (action.payload) {
        try {
          localStorage.setItem('currentDecisionTree', JSON.stringify(action.payload));
        } catch (error) {
          console.error('保存当前决策树到本地存储失败:', error);
        }
      }
      return newState;
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_PROGRESS':
      return { ...state, progress: action.payload };
    case 'SET_STREAMING_CONTENT':
      return { ...state, streamingContent: action.payload };
    case 'APPEND_STREAMING_CONTENT':
      return { ...state, streamingContent: state.streamingContent + action.payload };
    case 'RESET_STATE':
      // 从localStorage中删除当前决策树
      localStorage.removeItem('currentDecisionTree');
      return { 
        ...state, 
        decisionTree: null, 
        loading: false, 
        error: null, 
        progress: 0, 
        streamingContent: '',
        question: ''
      };
    default:
      return state;
  }
};

// Context类型定义
interface DecisionContextType {
  apiSettings: ApiSettings;
  updateApiSettings: (settings: Partial<ApiSettings>) => void;
  decisionTree: DecisionTree | null;
  loading: boolean;
  error: ApiError | null;
  progress: number;
  streamingContent: string;
  generateDecisions: (question: string) => Promise<void>;
  analyzeSubDecision: (parentDecision: Decision, subQuestion: string) => Promise<Decision[]>;
  saveDecisionTree: () => void;
  setDecisionTree: (tree: DecisionTree) => void;
  clearError: () => void;
  getAvailableModels: () => {id: string, name: string}[];
  resetState: () => void;
  question: string;
}

const DecisionContext = createContext<DecisionContextType | undefined>(undefined);

export const DecisionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(decisionReducer, initialState);
  const { apiSettings, decisionTree, loading, error, progress, streamingContent, question } = state;

  const updateApiSettings = useCallback((settings: Partial<ApiSettings>) => {
    dispatch({ type: 'SET_API_SETTINGS', payload: settings });
    
    // 保存到localStorage
    try {
      const updatedSettings = { ...apiSettings, ...settings };
      localStorage.setItem('apiSettings', JSON.stringify(updatedSettings));
    } catch (error) {
      console.error('保存API设置时出错:', error);
      dispatch({ 
        type: 'SET_ERROR', 
        payload: {
          code: 'STORAGE_ERROR',
          message: '保存设置失败',
          details: error instanceof Error ? error.message : String(error),
          timestamp: new Date()
        } 
      });
    }
  }, [apiSettings]);

  const resetState = useCallback(() => {
    dispatch({ type: 'RESET_STATE' });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  const getAvailableModels = useCallback(() => {
    return AIModelFactory.getAvailableModels();
  }, []);

  const generateDecisions = useCallback(async (question: string) => {
    resetState();
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      if (!apiSettings.apiKey) {
        throw new Error('请先设置API密钥');
      }

      const aiService = new AIService(apiSettings.adapterType, {
        apiKey: apiSettings.apiKey,
        baseUrl: apiSettings.baseUrl,
        model: apiSettings.model
      });

      const callbacks: StreamCallbacks = {
        onData: (data) => {
          dispatch({ type: 'APPEND_STREAMING_CONTENT', payload: data });
        },
        onProgress: (progress) => {
          dispatch({ type: 'SET_PROGRESS', payload: progress });
        },
        onComplete: (fullResponse) => {
          if (fullResponse) {
            try {
              const parsedTree = parseAIResponseToDecisionTree(fullResponse);
              dispatch({ 
                type: 'SET_DECISION_TREE', 
                payload: {
                  ...parsedTree,
                  question: question
                }
              });
            } catch (err) {
              dispatch({ 
                type: 'SET_ERROR', 
                payload: {
                  code: 'PARSE_ERROR',
                  message: '解析AI响应失败',
                  details: err instanceof Error ? err.message : String(err),
                  timestamp: new Date()
                } 
              });
            }
          }
          dispatch({ type: 'SET_LOADING', payload: false });
        },
        onError: (err) => {
          dispatch({ 
            type: 'SET_ERROR', 
            payload: {
              code: 'API_ERROR',
              message: '生成决策树失败',
              details: err instanceof Error ? err.message : String(err),
              timestamp: new Date()
            } 
          });
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      };

      await aiService.generateDecisionTree(question, apiSettings.prompt, callbacks);
    } catch (err) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: {
          code: 'GENERAL_ERROR',
          message: '生成决策树失败',
          details: err instanceof Error ? err.message : String(err),
          timestamp: new Date()
        } 
      });
      console.error('Failed to generate decision tree:', err);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [apiSettings, resetState]);

  // 新增：分析子决策的方法
  const analyzeSubDecision = useCallback(async (parentDecision: Decision, subQuestion: string): Promise<Decision[]> => {
    try {
      if (!apiSettings.apiKey) {
        throw new Error('请先设置API密钥');
      }

      dispatch({ type: 'SET_LOADING', payload: true });

      const aiService = new AIService(apiSettings.adapterType, {
        apiKey: apiSettings.apiKey,
        baseUrl: apiSettings.baseUrl,
        model: apiSettings.model
      });

      // 构建优化的提示词，包含父决策的上下文
      const contextPrompt = `
你是一个专业的决策分析助手。我需要你分析以下子决策问题，请确保考虑整个上下文。

当前分析的主要决策: "${parentDecision.title}"
该决策的成功概率: ${parentDecision.probability}%
该决策的详细描述: "${parentDecision.description}"

现在针对这个决策，我想继续分析以下问题: "${subQuestion}"

请根据上述上下文，为我生成3-5个针对此问题的子决策选项，并为每个选项分配成功概率（0-100%）。
每个子决策必须与父决策紧密相关，并考虑父决策的概率和描述信息。

请严格按照以下格式回复:

**选项 1: [子决策标题]** (成功概率: XX%)
[详细描述选项1的利弊和原因，并解释与父决策的关系]

**选项 2: [子决策标题]** (成功概率: XX%)
[详细描述选项2的利弊和原因，并解释与父决策的关系]

(以此类推...)
`;

      let subDecisions: Decision[] = [];
      
      // 调用AI服务，使用上下文提示词而不是默认提示词
      try {
        const response = await aiService.generateDecisionTree(subQuestion, contextPrompt, {
          onData: (data) => {
            dispatch({ type: 'APPEND_STREAMING_CONTENT', payload: data });
          },
          onProgress: (progress) => {
            dispatch({ type: 'SET_PROGRESS', payload: progress });
          },
          onComplete: (fullResponse) => {
            if (fullResponse) {
              try {
                // 解析响应
                const parsedResponse = parseAIResponseToDecisionTree(fullResponse);
                subDecisions = parsedResponse.decisions.map(d => ({
                  ...d,
                  id: `${parentDecision.id}-sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  children: []
                }));
                
                // 子决策按概率排序
                subDecisions.sort((a, b) => b.probability - a.probability);
              } catch (err) {
                console.error('解析AI子决策响应失败:', err);
                throw new Error('解析AI响应失败: ' + (err instanceof Error ? err.message : String(err)));
              }
            }
            dispatch({ type: 'SET_LOADING', payload: false });
          },
          onError: (err) => {
            dispatch({ 
              type: 'SET_ERROR', 
              payload: {
                code: 'API_ERROR',
                message: '生成子决策失败',
                details: err instanceof Error ? err.message : String(err),
                timestamp: new Date()
              } 
            });
            dispatch({ type: 'SET_LOADING', payload: false });
            
            // 提取API错误的原始信息，便于UI层识别具体错误类型
            const errMessage = err instanceof Error ? err.message : String(err);
            throw new Error(errMessage);
          }
        });
      } catch (apiError) {
        // 捕获API调用过程中的错误
        console.error('API调用错误:', apiError);
        dispatch({ type: 'SET_LOADING', payload: false });
        
        // 提取完整的错误信息，包括API密钥过期等具体原因
        let errorDetail = 'API调用失败';
        if (apiError instanceof Error) {
          if (apiError.message.includes('API key expired')) {
            errorDetail = 'API密钥已过期，请更新API密钥';
          } else {
            errorDetail = apiError.message;
          }
        }
        
        throw new Error(errorDetail);
      }
      
      return subDecisions;
    } catch (err) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: {
          code: 'GENERAL_ERROR',
          message: '生成子决策失败',
          details: err instanceof Error ? err.message : String(err),
          timestamp: new Date()
        } 
      });
      console.error('Failed to generate sub-decisions:', err);
      dispatch({ type: 'SET_LOADING', payload: false });
      throw err;
    }
  }, [apiSettings]);

  // 迁移旧版数据格式
  const migrateOldRecordsFormat = useCallback(() => {
    try {
      const existingRecordsJson = localStorage.getItem('decisionRecords');
      if (existingRecordsJson) {
        let hasChanges = false;
        const records = JSON.parse(existingRecordsJson);
        
        // 遍历所有记录，检查是否需要迁移
        const migratedRecords = records.map((record: any) => {
          // 如果存在tree字段但不存在decisionTree字段
          if (record.tree && !record.decisionTree) {
            hasChanges = true;
            return {
              ...record,
              decisionTree: record.tree,
              tree: undefined // 删除旧字段
            };
          }
          return record;
        });
        
        // 只有在有变更时才保存回localStorage
        if (hasChanges) {
          localStorage.setItem('decisionRecords', JSON.stringify(migratedRecords));
          console.log('已迁移旧版决策记录格式');
        }
      }
    } catch (error) {
      console.error('迁移决策记录失败:', error);
    }
  }, []);

  // 在组件挂载时执行数据迁移
  useEffect(() => {
    migrateOldRecordsFormat();
  }, [migrateOldRecordsFormat]);

  const saveDecisionTree = useCallback(() => {
    if (!state.decisionTree) {
      alert("没有决策树可以保存");
      return;
    }
    
    // 从会话存储中获取当前问题（如果存在）或使用状态中的问题
    // 最后尝试直接从decisionTree中获取question
    const questionFromSession = sessionStorage.getItem('currentQuestion');
    const question = questionFromSession || state.question || (state.decisionTree.question ? state.decisionTree.question : '');
    
    if (!question) {
      alert("请先提供一个问题");
      return;
    }

    // 创建记录对象
    const record = {
      id: uuidv4(),  // 生成唯一ID
      timestamp: new Date().toISOString(),
      question,
      decisionTree: state.decisionTree,
      tags: []
    };

    // 获取现有记录
    const existingRecordsJson = localStorage.getItem('decisionRecords');
    let records = existingRecordsJson ? JSON.parse(existingRecordsJson) : [];

    // 检查是否已存在相同问题的记录
    const existingRecordIndex = records.findIndex((r: any) => r.question === question);

    if (existingRecordIndex !== -1) {
      // 如果存在，则替换现有记录（但保留原始ID和标签）
      const originalId = records[existingRecordIndex].id;
      const originalTags = records[existingRecordIndex].tags || [];
      
      record.id = originalId; // 保留原始ID
      record.tags = originalTags; // 保留原始标签
      
      records[existingRecordIndex] = record;
      alert("决策记录已更新");
    } else {
      // 如果不存在，则添加新记录
      // 限制记录数量，避免超出localStorage限制
      if (records.length >= 100) {
        records.pop(); // 移除最旧的记录
      }
      records.unshift(record); // 添加到列表前面
      alert("决策记录已保存");
    }

    // 保存回localStorage
    localStorage.setItem('decisionRecords', JSON.stringify(records));
    
    // 在保存后清除会话存储中的临时问题
    if (questionFromSession) {
      sessionStorage.removeItem('currentQuestion');
    }
  }, [state.decisionTree, state.question]);

  const setDecisionTree = useCallback((tree: DecisionTree) => {
    dispatch({ type: 'SET_DECISION_TREE', payload: tree });
  }, []);

  const value = {
    apiSettings,
    updateApiSettings,
    decisionTree,
    loading,
    error,
    progress,
    streamingContent,
    generateDecisions,
    analyzeSubDecision,
    saveDecisionTree,
    setDecisionTree,
    clearError,
    getAvailableModels,
    resetState,
    question
  };

  return (
    <DecisionContext.Provider value={value}>
      {children}
    </DecisionContext.Provider>
  );
};

export const useDecisionContext = () => {
  const context = useContext(DecisionContext);
  if (context === undefined) {
    throw new Error('useDecisionContext must be used within a DecisionProvider');
  }
  return context;
}; 